import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPageMeta } from "@/lib/seo-meta";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const setMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]');
  links.forEach((l, i) => i > 0 && l.remove());
  let el = links[0];
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setRobots = (noindex: boolean) => {
  const el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (noindex) {
    setMeta("name", "robots", "noindex, nofollow");
  } else if (el) {
    el.remove();
  }
};

/**
 * Applies per-route title, description, canonical and matching og:/twitter: tags.
 * The homepage keeps the CMS-managed site-wide title/description when set.
 */
export function RouteMeta() {
  const { pathname } = useLocation();
  const { settings } = useSiteSettings();

  useEffect(() => {
    const meta = getPageMeta(pathname);
    const isHome = pathname === "/" || pathname === "/home" || pathname === "/landing";

    const title = (isHome && settings.seoTitle) || meta.title;
    const description = (isHome && settings.seoDescription) || meta.description;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", meta.canonical);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setCanonical(meta.canonical);
    setRobots(Boolean(meta.noindex));
  }, [pathname, settings.seoTitle, settings.seoDescription]);

  return null;
}
